import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from 'react-router-dom';
import ApiURl from "../Paths/Api";

// SVG Icons (unchanged)
const SearchIcon = () => (
  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const EyeIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);

const DownloadIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const PrintIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
  </svg>
);

const CardPrintIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
  </svg>
);

const ChevronLeftIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const ChevronRightIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);

const MeetingReport = () => {
  const [meetings, setMeetings] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedMeetingId, setSelectedMeetingId] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [expandedAttendee, setExpandedAttendee] = useState(null);
  const [attendeeStatusFilter, setAttendeeStatusFilter] = useState("all");
  const [attendeeSearch, setAttendeeSearch] = useState("");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const navigate = useNavigate();

  // Fetch meetings
  const fetchMeetings = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const phone = localStorage.getItem('phone');
      if (!phone) {
        throw new Error("Phone number not found in local storage");
      }

      const response = await fetch(`${ApiURl}/get_meetings.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const result = await response.json();
      if (result.success) {
        setMeetings(result.data);
      } else {
        throw new Error(result.message || "Failed to fetch meetings");
      }
    } catch (err) {
      setError(err.message);
      console.error("Error fetching meetings:", err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMeetings();
  }, [fetchMeetings]);

  // Toggle dark mode
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
    document.documentElement.classList.toggle('dark');
  };

  // Filter meetings
  const filteredMeetings = meetings.filter((meeting) => {
    const title = meeting.title || "";
    const purpose = meeting.purpose || "";
    const status = meeting.status || "";
    const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      purpose.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || status.toLowerCase() === statusFilter.toLowerCase();
    return matchesSearch && matchesStatus;
  });

  // Get selected meeting
  const selectedMeeting = meetings.find((meeting) => String(meeting.id) === String(selectedMeetingId));

  // Filter attendees
  const filteredAttendees = selectedMeeting ? (selectedMeeting.attendees || []).filter((attendee) => {
    const name = attendee.name || "";
    const email = attendee.email || "";
    const phone = attendee.phone || "";
    const matchesSearch = name.toLowerCase().includes(attendeeSearch.toLowerCase()) ||
      email.toLowerCase().includes(attendeeSearch.toLowerCase()) ||
      phone.toLowerCase().includes(attendeeSearch.toLowerCase());

    if (attendeeStatusFilter === "all") return matchesSearch;
    if (attendeeStatusFilter === "attended") return matchesSearch && attendee.attended;
    if (attendeeStatusFilter === "not_attended") return matchesSearch && !attendee.attended;
    if (attendeeStatusFilter === "first_scan") return matchesSearch && attendee.scan_status === 1;
    if (attendeeStatusFilter === "second_scan") return matchesSearch && attendee.scan_status === 2;

    return matchesSearch;
  }) : [];

  // Pagination calculations
  const totalPages = Math.ceil(filteredAttendees.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAttendees = filteredAttendees.slice(startIndex, endIndex);

  // Pagination handlers
  const goToPage = (page) => {
    setCurrentPage(page);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedMeetingId, attendeeSearch, attendeeStatusFilter]);

  // Function to extract attendee type from custom form data
  const getAttendeeType = (attendee) => {
    // Check if form_data exists and is a string (JSON)
    if (attendee.form_data) {
      try {
        const formData = typeof attendee.form_data === 'string'
          ? JSON.parse(attendee.form_data)
          : attendee.form_data;

        // Look for common field names that might indicate attendee type
        const possibleFields = [
          'attendee_type', 'attendeeType', 'type', 'category',
          'role', 'designation', 'position', 'registration_type'
        ];

        for (const field of possibleFields) {
          if (formData[field]) {
            return String(formData[field]);
          }
        }
      } catch (e) {
        console.error("Error parsing form_data:", e);
      }
    }

    // Check custom_form_data if available
    if (attendee.custom_form_data && Array.isArray(attendee.custom_form_data)) {
      const typeField = attendee.custom_form_data.find(field =>
        ['attendee_type', 'type', 'role', 'category'].includes(field.field_name?.toLowerCase())
      );
      if (typeField) {
        return typeField.field_value;
      }
    }

    // Default to role if available
    return attendee.role || "Guest";
  };

  // Function to extract branch/company from custom form data
  const getAttendeeBranch = (attendee) => {
    // Check if form_data exists and is a string (JSON)
    if (attendee.form_data) {
      try {
        const formData = typeof attendee.form_data === 'string'
          ? JSON.parse(attendee.form_data)
          : attendee.form_data;

        // Look for common field names that might indicate branch/company
        const possibleFields = [
          'branch', 'company', 'organization', 'institution',
          'college', 'school', 'department', 'division'
        ];

        for (const field of possibleFields) {
          if (formData[field]) {
            return String(formData[field]);
          }
        }
      } catch (e) {
        console.error("Error parsing form_data:", e);
      }
    }

    // Check custom_form_data if available
    if (attendee.custom_form_data && Array.isArray(attendee.custom_form_data)) {
      const branchField = attendee.custom_form_data.find(field =>
        ['branch', 'company', 'organization'].includes(field.field_name?.toLowerCase())
      );
      if (branchField) {
        return branchField.field_value;
      }
    }

    // Default
    return "MAIN";
  };

  // Export to CSV
  const exportToCSV = () => {
    if (!selectedMeeting) {
      alert("Please select a meeting to export.");
      return;
    }

    const attendees = selectedMeeting.attendees || [];
    const headers = [
      "S.No", "Name", "Email", "Phone", "Attendee Type", "Role", "Invite Code", "Unique Code",
      "Scan Status", "Scan Level", "First Scan Time", "Second Scan Time",
      "Verified", "Verification Time", "Ticket ID", "Payment ID", "Order ID",
      "Address", "Pincode"
    ];

    const csvRows = [headers.join(",")];

    attendees.forEach((attendee, index) => {
      const attendeeType = getAttendeeType(attendee);
      const row = [
        index + 1,
        `"${(attendee.name || "Unknown").replace(/"/g, '""')}"`,
        `"${(attendee.email || "N/A").replace(/"/g, '""')}"`,
        `"${(attendee.phone || "N/A").replace(/"/g, '""')}"`,
        `"${attendeeType.replace(/"/g, '""')}"`,
        `"${(attendee.role || "User").replace(/"/g, '""')}"`,
        `"${(attendee.invite_code || "N/A").replace(/"/g, '""')}"`,
        `"${(attendee.unique_code || "N/A").replace(/"/g, '""')}"`,
        attendee.scan_status || "0",
        attendee.scan_level || "not_scanned",
        `"${attendee.first_scan_time || "-"}"`,
        `"${attendee.second_scan_time || "-"}"`,
        attendee.verified_by_organizer ? "Yes" : "No",
        `"${attendee.verification_time || "-"}"`,
        `"${(attendee.ticket_id || "N/A").replace(/"/g, '""')}"`,
        `"${(attendee.payment_id || "N/A").replace(/"/g, '""')}"`,
        `"${(attendee.order_id || "N/A").replace(/"/g, '""')}"`,
        `"${(attendee.address || "N/A").replace(/"/g, '""')}"`,
        `"${(attendee.pincode || "N/A").replace(/"/g, '""')}"`
      ];
      csvRows.push(row.join(","));
    });

    const csvString = csvRows.join("\n");
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `meeting_${selectedMeeting.title || 'report'}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Calculate stats
  const calculateStats = (attendees) => {
    const total = attendees.length;
    const attended = attendees.filter(a => a.attended).length;
    const firstScan = attendees.filter(a => a.scan_status === 1).length;
    const secondScan = attendees.filter(a => a.scan_status === 2).length;
    const verified = attendees.filter(a => a.verified_by_organizer).length;

    return { total, attended, firstScan, secondScan, verified };
  };

  // CARD PRINT FUNCTION - EXACT DESIGN AS YOU PROVIDED
  const printCard = (attendee) => {
    if (!selectedMeeting || !attendee) return;

    const printWindow = window.open('', '_blank');

    // Card dimensions in millimeters (111mm × 155mm)
    const cardWidth = 111;
    const cardHeight = 155;

    // Exact measurements from your card - adjusted slightly lower
    const vacantAreaTop = 85.0; // mm - moved slightly lower
    const vacantAreaHeight = 21.8; // mm (2.28cm height)

    const attendeeType = getAttendeeType(attendee);
    const attendeeBranch = getAttendeeBranch(attendee);

    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Print Card</title>
      <style>
        /* PRINT STYLES ONLY */
        @media print {
          @page {
            size: ${cardWidth}mm ${cardHeight}mm;
            margin: 0;
            padding: 0;
          }
          
          html, body {
            width: ${cardWidth}mm;
            height: ${cardHeight}mm;
            margin: 0;
            padding: 0;
            background: transparent;
          }
          
          .print-container {
            position: relative;
            width: ${cardWidth}mm;
            height: ${cardHeight}mm;
          }
          
          .print-area {
            position: absolute;
            top: ${vacantAreaTop}mm;
            left: 0;
            width: ${cardWidth}mm;
            height: ${vacantAreaHeight}mm;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
            text-align: center;
            padding: 0 5mm;
            box-sizing: border-box;
          }
          
          .attendee-name {
            font-size: 14pt;
            font-weight: 900;
            color: #000000;
            margin: 0;
            padding: 0;
            text-transform: uppercase;
            letter-spacing: 0.2mm;
            line-height: 0.9;
            height: 7.5mm;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'Arial Black', sans-serif;
            text-shadow: none;
            -webkit-text-stroke: 0;
          }
          
          .attendee-type {
            font-size: 11.5pt;
            font-weight: 900;
            color: #000000;
            margin: -0.2mm 0 0 0;
            padding: 0;
            text-transform: uppercase;
            letter-spacing: 0.15mm;
            line-height: 0.9;
            height: 6.2mm;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'Arial Black', sans-serif;
          }
          
          .attendee-branch {
            font-size: 8.5pt;
            font-weight: 900;
            color: #000000;
            margin: -0.2mm 0 0 0;
            padding: 0;
            text-transform: uppercase;
            letter-spacing: 0.15mm;
            line-height: 0.9;
            height: 5.5mm;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'Arial Black', sans-serif;
          }
          
          /* HIDE EVERYTHING WHEN PRINTING */
          .no-print, .no-print * {
            display: none !important;
            visibility: hidden !important;
          }
        }
        
        /* SCREEN PREVIEW STYLES */
        @media screen {
          body {
            margin: 0;
            padding: 20px;
            background: #f0f0f0;
            display: flex;
            justify-content: center;
            align-items: flex-start;
            min-height: 100vh;
          }
          
          .print-container {
            width: ${cardWidth}mm;
            height: ${cardHeight}mm;
            background: white;
            position: relative;
            border: 1px solid #ccc;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
            transform: scale(0.8);
            transform-origin: top center;
          }
          
          .print-area {
            position: absolute;
            top: ${vacantAreaTop}mm;
            left: 0;
            width: ${cardWidth}mm;
            height: ${vacantAreaHeight}mm;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
            text-align: center;
            padding: 0 5mm;
            box-sizing: border-box;
            border: 1px dashed red;
            background: rgba(255,0,0,0.05);
          }
          
          .attendee-name {
            font-size: 12pt;
            font-weight: 900;
            color: #000000;
            margin: 0;
            padding: 0;
            text-transform: uppercase;
            letter-spacing: 0.2mm;
            line-height: 0.9;
            height: 7.5mm;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'Arial Black', sans-serif;
          }
          
          .attendee-type {
            font-size: 9.5pt;
            font-weight: 900;
            color: #000000;
            margin: -0.2mm 0 0 0;
            padding: 0;
            text-transform: uppercase;
            letter-spacing: 0.15mm;
            line-height: 0.9;
            height: 6.2mm;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'Arial Black', sans-serif;
          }
          
          .attendee-branch {
            font-size: 8.5pt;
            font-weight: 900;
            color: #000000;
            margin: -0.2mm 0 0 0;
            padding: 0;
            text-transform: uppercase;
            letter-spacing: 0.15mm;
            line-height: 0.9;
            height: 5.5mm;
            display: flex;
            align-items: center;
            justify-content: center;
            font-family: 'Arial Black', sans-serif;
          }
          
          .preview-guide {
            position: fixed;
            top: 10px;
            left: 10px;
            background: rgba(0,0,0,0.8);
            color: white;
            padding: 10px;
            font-size: 12px;
            border-radius: 5px;
            z-index: 1000;
          }
        }
      </style>
    </head>
    <body>
      <!-- Preview guide - only shows on screen, hidden when printing -->
      <div class="no-print preview-guide">
        PREVIEW: Card ${cardWidth}mm × ${cardHeight}mm | Print area starts at ${vacantAreaTop}mm (${vacantAreaHeight}mm height)
      </div>
      
      <div class="print-container">
        <div class="print-area">
          <div class="attendee-name">${attendee.name || 'GUEST'}</div>
          <div class="attendee-type">${attendeeType || 'ATTENDEE'}</div>
        </div>
      </div>
      
      <script>
        window.onload = function() {
          // Auto print after a short delay
          setTimeout(function() {
            window.print();
          }, 500);
          
          // Close window after printing
          window.onafterprint = function() {
            window.close();
          };
        }
      </script>
    </body>
    </html>
  `);
    printWindow.document.close();
  };

  // REPLACE THE OLD PRINT RECEIPT FUNCTION WITH CARD PRINT
  const printReceipt = (attendee) => {
    // Now using the card print design instead of receipt design
    printCard(attendee);
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDarkMode ? 'dark bg-gray-900' : 'bg-gray-50'} font-sans`}>
      {/* Navbar */}
      <nav className="bg-gradient-to-r from-teal-600 to-cyan-600 dark:from-teal-800 dark:to-cyan-800 text-white p-4 shadow-xl fixed w-full z-20">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="mr-4 md:hidden hover:bg-teal-700 dark:hover:bg-teal-900 p-2 rounded transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <h1 className="text-xl font-bold">Event Reports</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={toggleDarkMode}
              className="p-2 rounded hover:bg-teal-700 dark:hover:bg-teal-900 transition-all"
            >
              {isDarkMode ? '🌙' : '☀️'}
            </button>
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-1.5 bg-red-500 rounded hover:bg-red-600 text-sm shadow"
            >
              Back
            </button>
          </div>
        </div>
      </nav>

      {/* Sidebar */}
      <div
        className={`fixed top-14 left-0 h-[calc(100vh-3.5rem)] bg-gradient-to-b from-teal-600 to-teal-800 text-white w-16 flex flex-col items-center py-4 space-y-4 transition-transform duration-300 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'} md:translate-x-0 md:w-16 z-20`}
      >
        <button
          className="p-2 rounded-full bg-teal-700 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Dashboard"
          onClick={() => navigate('/dashboard')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Dashboard</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Create Meeting"
          onClick={() => navigate('/create-meeting')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Create Meeting</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="View Meetings"
          onClick={() => navigate('/view-meetings')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Reports</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Send Invites"
          onClick={() => navigate('/send-invites')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7-4 7 4v8l-7 4-7-4V8zm0 0l7 4 7-4m-14 0v8l7 4 7-4V8" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Invite</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Search Meetings"
          onClick={() => navigate('/search-form')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Search</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Send Invite Without Pay"
          onClick={() => navigate('/adminsend')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7-4 7 4v8l-7 4-7-4V8zm0 0l7 4 7-4m-14 0v8l7 4 7-4V8" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Send Invite</span>
        </button>
        <button
          className="p-2 rounded-full hover:bg-teal-600 transition-all duration-300 transform hover:scale-110 flex items-center w-full justify-center group"
          title="Restrict Meeting"
          onClick={() => navigate('/restrict-meeting')}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 1.104-.896 2-2 2s-2-.896-2-2 2-4 2-4 2 2.896 2 4zm0 0c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4zm-6 7c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4zm12 0c0 1.104.896 2 2 2s2-.896 2-2-2-4-2-4-2 2.896-2 4z" />
          </svg>
          <span className="absolute left-16 bg-teal-700 text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 hidden md:block">Restrict Meeting</span>
        </button>
      </div>

      {/* Main Content */}
      <div className="pt-14 md:pl-16 p-4 max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-4">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Meeting Reports</h1>
              <p className="text-sm text-gray-600 dark:text-gray-400">View and export attendance data</p>
            </div>
            <button
              onClick={fetchMeetings}
              className="px-3 py-1.5 bg-teal-600 text-white rounded hover:bg-teal-700 text-sm"
            >
              Refresh
            </button>
          </div>

          {error && (
            <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 rounded border border-red-200 dark:border-red-700 text-sm">
              {error}
              <button onClick={fetchMeetings} className="ml-2 text-red-600 dark:text-red-400 underline">
                Try Again
              </button>
            </div>
          )}
        </div>

        {/* Meeting Selection Card */}
        <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow mb-4 border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-3">
            <div className="relative">
              <input
                type="text"
                placeholder="Search meetings..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 text-sm dark:text-white"
              />
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                <SearchIcon />
              </div>
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 text-sm dark:text-white"
            >
              <option value="all">All Status</option>
              <option value="scheduled">Scheduled</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
            </select>

            <select
              value={selectedMeetingId}
              onChange={(e) => setSelectedMeetingId(e.target.value)}
              className="px-3 py-2 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 text-sm dark:text-white"
            >
              <option value="">Select Meeting</option>
              {filteredMeetings.map((meeting) => (
                <option key={meeting.id} value={meeting.id}>
                  {meeting.title || meeting.purpose} - {meeting.time ? new Date(meeting.time.replace(" ", "T")).toLocaleDateString() : ''}
                </option>
              ))}
            </select>

            <button
              onClick={exportToCSV}
              disabled={!selectedMeeting}
              className="px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 flex items-center justify-center gap-2 text-sm disabled:opacity-50"
            >
              <DownloadIcon />
              Export CSV
            </button>
          </div>

          {/* Quick Stats */}
          {selectedMeeting && (
            <div className="flex flex-wrap gap-2 mb-3">
              <span className="px-2 py-1 bg-teal-100 dark:bg-teal-900 text-teal-800 dark:text-teal-200 rounded text-xs">
                📅 {selectedMeeting.time ? new Date(selectedMeeting.time.replace(" ", "T")).toLocaleDateString() : ''}
              </span>
              <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded text-xs">
                📍 {selectedMeeting.location || 'N/A'}
              </span>
              <span className={`px-2 py-1 rounded text-xs ${selectedMeeting.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                selectedMeeting.status === 'scheduled' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                  'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
                }`}>
                {selectedMeeting.status || 'N/A'}
              </span>
            </div>
          )}
        </div>

        {/* Selected Meeting Details */}
        {selectedMeeting && (
          <div className="space-y-4">
            {/* Stats Cards */}
            {selectedMeeting.attendees && selectedMeeting.attendees.length > 0 && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded">
                  <div className="text-lg font-bold text-blue-600 dark:text-blue-300">{filteredAttendees.length}</div>
                  <div className="text-xs text-gray-600 dark:text-gray-300">Total</div>
                </div>
                <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded">
                  <div className="text-lg font-bold text-green-600 dark:text-green-300">
                    {calculateStats(selectedMeeting.attendees).attended}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-300">Attended</div>
                </div>
                <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded">
                  <div className="text-lg font-bold text-yellow-600 dark:text-yellow-300">
                    {calculateStats(selectedMeeting.attendees).firstScan}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-300">First Scan</div>
                </div>
                <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded">
                  <div className="text-lg font-bold text-purple-600 dark:text-purple-300">
                    {calculateStats(selectedMeeting.attendees).secondScan}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-300">Second Scan</div>
                </div>
                <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded">
                  <div className="text-lg font-bold text-indigo-600 dark:text-indigo-300">
                    {calculateStats(selectedMeeting.attendees).verified}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-300">Verified</div>
                </div>
              </div>
            )}

            {/* Attendee Filters and Table */}
            <div className="bg-white dark:bg-gray-800 p-3 rounded-lg shadow border border-gray-200 dark:border-gray-700">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 mb-3">
                <div className="flex-1 w-full md:w-auto">
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="Search attendees..."
                      value={attendeeSearch}
                      onChange={(e) => setAttendeeSearch(e.target.value)}
                      className="w-full pl-9 pr-3 py-1.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 text-sm dark:text-white"
                    />
                    <div className="absolute inset-y-0 left-0 pl-2.5 flex items-center">
                      <SearchIcon />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <select
                    value={attendeeStatusFilter}
                    onChange={(e) => setAttendeeStatusFilter(e.target.value)}
                    className="px-3 py-1.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 text-sm dark:text-white"
                  >
                    <option value="all">All Attendees</option>
                    <option value="attended">Attended</option>
                    <option value="not_attended">Not Attended</option>
                    <option value="first_scan">First Scan</option>
                    <option value="second_scan">Second Scan</option>
                  </select>

                  <select
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    className="px-3 py-1.5 bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded focus:outline-none focus:ring-1 focus:ring-teal-500 text-sm dark:text-white"
                  >
                    <option value="10">10 per page</option>
                    <option value="25">25 per page</option>
                    <option value="50">50 per page</option>
                    <option value="100">100 per page</option>
                  </select>
                </div>
              </div>

              {/* Attendees Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="px-3 py-2.5 text-left font-medium text-gray-700 dark:text-gray-200">#</th>
                      <th className="px-3 py-2.5 text-left font-medium text-gray-700 dark:text-gray-200">Name</th>
                      <th className="px-3 py-2.5 text-left font-medium text-gray-700 dark:text-gray-200">Attendee Type</th>
                      <th className="px-3 py-2.5 text-left font-medium text-gray-700 dark:text-gray-200">Contact</th>
                      <th className="px-3 py-2.5 text-left font-medium text-gray-700 dark:text-gray-200">Status</th>
                      <th className="px-3 py-2.5 text-left font-medium text-gray-700 dark:text-gray-200">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedAttendees.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-3 py-4 text-center text-gray-500 dark:text-gray-400">
                          No attendees found
                        </td>
                      </tr>
                    ) : (
                      paginatedAttendees.map((attendee, index) => {
                        const attendeeType = getAttendeeType(attendee);
                        const globalIndex = startIndex + index + 1;

                        return (
                          <React.Fragment key={attendee.id}>
                            <tr className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                              <td className="px-3 py-2.5 font-medium">{globalIndex}</td>
                              <td className="px-3 py-2.5 font-medium">
                                <div>{attendee.name || 'Unknown'}</div>
                                {attendee.invite_code && (
                                  <div className="text-xs text-gray-500 dark:text-gray-400">
                                    Invite: {attendee.invite_code}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2.5">
                                <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded text-xs">
                                  {attendeeType}
                                </span>
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="font-medium">{attendee.phone || 'N/A'}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{attendee.email || ''}</div>
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="flex flex-col gap-0.5">
                                  <span className={`px-1.5 py-0.5 rounded text-xs w-fit ${attendee.scan_status === 2 ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                                    attendee.scan_status === 1 ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                      'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                    }`}>
                                    {attendee.scan_level ? attendee.scan_level.replace(/_/g, ' ') : 'Not Scanned'}
                                  </span>
                                  {attendee.first_scan_time && (
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                      {new Date(attendee.first_scan_time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-3 py-2.5">
                                <div className="flex gap-1">
                                  <button
                                    onClick={() => setExpandedAttendee(expandedAttendee === attendee.id ? null : attendee.id)}
                                    className="p-1 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                    title="View Details"
                                  >
                                    <EyeIcon />
                                  </button>
                                  <button
                                    onClick={() => printCard(attendee)}
                                    className="p-1 text-teal-600 dark:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded"
                                    title="Print Card"
                                  >
                                    <CardPrintIcon />
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {expandedAttendee === attendee.id && (
                              <tr>
                                <td colSpan="6" className="px-3 py-3 bg-gray-50 dark:bg-gray-700/30">
                                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                                    <div>
                                      <div className="font-medium mb-1">Personal Info</div>
                                      <div><span className="text-gray-500">Role:</span> {attendee.role || 'N/A'}</div>
                                      <div><span className="text-gray-500">Address:</span> {attendee.address || 'N/A'}</div>
                                      <div><span className="text-gray-500">Pincode:</span> {attendee.pincode || 'N/A'}</div>
                                    </div>
                                    <div>
                                      <div className="font-medium mb-1">Scan Details</div>
                                      <div><span className="text-gray-500">First Scan:</span> {attendee.first_scan_time || 'Not Scanned'}</div>
                                      <div><span className="text-gray-500">Second Scan:</span> {attendee.second_scan_time || 'Not Scanned'}</div>
                                      <div><span className="text-gray-500">Verified:</span> {attendee.verified_by_organizer ? 'Yes' : 'No'}</div>
                                      <div><span className="text-gray-500">Verification Time:</span> {attendee.verification_time || 'N/A'}</div>
                                    </div>
                                    <div>
                                      <div className="font-medium mb-1">Ticket Info</div>
                                      <div><span className="text-gray-500">Unique Code:</span> {attendee.unique_code || 'N/A'}</div>
                                      <div><span className="text-gray-500">Ticket ID:</span> {attendee.ticket_id || 'N/A'}</div>
                                      <div><span className="text-gray-500">Payment ID:</span> {attendee.payment_id || 'N/A'}</div>
                                      <div><span className="text-gray-500">Order ID:</span> {attendee.order_id || 'N/A'}</div>
                                    </div>
                                  </div>
                                </td>
                              </tr>
                            )}
                          </React.Fragment>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex flex-col md:flex-row justify-between items-center mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                  <div className="text-sm text-gray-600 dark:text-gray-400 mb-2 md:mb-0">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredAttendees.length)} of {filteredAttendees.length} attendees
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={goToPreviousPage}
                      disabled={currentPage === 1}
                      className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeftIcon />
                    </button>

                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => {
                        // Show first, last, and pages around current page
                        if (totalPages <= 7) return true;
                        if (page === 1 || page === totalPages) return true;
                        if (Math.abs(page - currentPage) <= 2) return true;
                        return false;
                      })
                      .map((page, index, array) => {
                        // Add ellipsis
                        const prevPage = array[index - 1];
                        if (prevPage && page - prevPage > 1) {
                          return (
                            <React.Fragment key={`ellipsis-${page}`}>
                              <span className="px-2 text-gray-500">...</span>
                              <button
                                onClick={() => goToPage(page)}
                                className={`px-2.5 py-1 rounded text-sm ${currentPage === page ? 'bg-teal-600 text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                              >
                                {page}
                              </button>
                            </React.Fragment>
                          );
                        }

                        return (
                          <button
                            key={page}
                            onClick={() => goToPage(page)}
                            className={`px-2.5 py-1 rounded text-sm ${currentPage === page ? 'bg-teal-600 text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                          >
                            {page}
                          </button>
                        );
                      })}

                    <button
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                      className="p-1.5 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRightIcon />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingReport;